import { Link } from "react-router-dom";
import { Shield, Mail, Send } from "lucide-react";
import { Twitter, Instagram, Linkedin, Facebook } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { CookieSettingsButton } from "./CookieConsent";
import { useFooterSettings } from "@/hooks/useFooterSettings";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { settings, isLoading: settingsLoading } = useFooterSettings();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('newsletter_subscripts')
        .insert([{ email }]);

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Déjà inscrit",
            description: "Cette adresse email est déjà inscrite à notre newsletter.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Inscription réussie !",
          description: "Vous êtes maintenant inscrit à notre newsletter.",
        });
        setEmail("");
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de l'inscription.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSocialIcon = (platform: string) => {
    const icons: Record<string, any> = {
      X: Twitter,
      Twitter: Twitter,
      Instagram: Instagram,
      LinkedIn: Linkedin,
      Facebook: Facebook
    };
    return icons[platform] || Twitter;
  };

  return (
    <footer className="bg-muted border-t border-card-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <Shield className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary">{settings.company_name}</span>
            </Link>
            <p className="text-muted-foreground mb-4 max-w-md">
              {settings.description}
            </p>
            <div className="space-y-2">
              <div className="flex items-center text-sm text-muted-foreground">
                <Mail className="h-4 w-4 mr-2" />
                <a href={`mailto:${settings.email}`} className="hover:text-primary transition-colors">
                  {settings.email}
                </a>
              </div>
              {settings.show_social_media && Array.isArray(settings.social_media) && settings.social_media.some(s => s?.enabled) && (
                <div className="flex items-center gap-3 pt-2">
                  {settings.social_media.filter(s => s?.enabled).map((social, idx) => {
                    const Icon = getSocialIcon(social.platform || '');
                    return (
                      <a
                        key={idx}
                        href={social.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        aria-label={social.platform || 'social'}
                      >
                        <Icon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Newsletter</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Recevez nos derniers guides et conseils sur l'assurance Dommages-Ouvrage.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-2">
              <Input
                type="email"
                placeholder="Votre email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="text-sm"
              />
              <Button 
                type="submit" 
                size="sm" 
                disabled={isLoading}
                className="w-full"
              >
                <Send className="h-3 w-3 mr-2" />
                {isLoading ? "Inscription..." : "S'inscrire"}
              </Button>
            </form>
          </div>

          {/* Dynamic sections */}
          {!settingsLoading && Array.isArray(settings.sections) && settings.sections.slice(0, 2).map((section, index) => (
            <div key={index}>
              <h3 className="font-semibold text-foreground mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {(section.links || []).map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.url.startsWith('mailto:') ? (
                      <a 
                        href={link.url} 
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link 
                        to={link.url} 
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>

        {/* Section liens légaux en bas */}
        <div className="border-t border-card-border mt-8 pt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
            {!settingsLoading && Array.isArray(settings.sections) && settings.sections.slice(2).map((section, index) => (
              <div key={index}>
                <h4 className="font-medium text-foreground mb-3 text-sm">{section.title}</h4>
                <ul className="space-y-1">
                  {(section.links || []).map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {link.url.startsWith('mailto:') ? (
                        <a 
                          href={link.url} 
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {link.text}
                        </a>
                      ) : (
                        <Link 
                          to={link.url} 
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                        >
                          {link.text}
                        </Link>
                      )}
                    </li>
                  ))}
                  {section.title === 'Légal' && (
                    <li>
                      <CookieSettingsButton />
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center pt-6 border-t border-card-border">
          <p className="text-muted-foreground text-sm">
            {settings.copyright_text.replace('{year}', new Date().getFullYear().toString())}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;